import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, TextInput, View } from 'react-native';
import { Text } from '../components/Texte';
import { SafeAreaView } from 'react-native-safe-area-context';
import { PAIEMENT_DISPONIBLE, usePaiementCoaching } from '../services/stripe';
import { URL_RETOUR_PAIEMENT } from '../navigation/liensWeb';
import { capturerIncident } from '../services/sentry';
import { verifierMontant } from '../services/coherenceMontant';
import { useTarifsCoaching } from '../hooks/useTarifsCoaching';
import { libellePrix, tarifDe, tarifPret } from '../services/tarifsCoaching';
import { CalendarCheck, TriangleAlert } from '../components/icons';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { useGabarit } from '../hooks/useGabarit';
import { ScreenHeader } from '../components/ScreenHeader';
import { useProfile } from '../hooks/useProfile';
import { getAuthEmail } from '../services/profile';
import { createCoachingPaymentIntent } from '../services/coachingPayment';
import { waitForCoachingBooking, type CoachingBooking } from '../services/coachingBooking';
import { COACHING_FORMULES, type CoachingFormuleId } from '../config/coaching';
import { STRIPE_MERCHANT_NAME } from '../config/stripe';
import { isValidEmail } from '../utils/validators';
import { colonneFormulaire, colors, layout, radius, screenPadding, spacing, typography } from '../theme/tokens';
import { t } from '../i18n';

export interface CoachingReservationScreenProps {
  formule: CoachingFormuleId;
  /** Identifiant Cal.com du créneau réservé, quand on arrive depuis le calendrier. */
  bookingUid?: string;
  onBack?: () => void;
}

// `processing` est un état à part entière, ni `paid` ni `error` : le paiement est parti, la banque
// n'a pas tranché. Le confondre avec `error` inviterait à repayer un paiement déjà en cours.
type PayState = 'idle' | 'paying' | 'paid' | 'processing' | 'canceled' | 'error';
// État de la VÉRIFICATION SERVEUR du créneau : c'est elle qui autorise le paiement.
type SlotState = 'none' | 'checking' | 'confirmed' | 'missing' | 'cancelled';

export function CoachingReservationScreen({ formule, bookingUid, onBack }: CoachingReservationScreenProps) {
  const { profile } = useProfile();
  const { payer, Formulaire } = usePaiementCoaching();
  const { etat: etatTarifs, reessayer } = useTarifsCoaching();
  const f = COACHING_FORMULES.find((x) => x.id === formule);

  // Nom / email / message restent saisis mais ne sont plus envoyés au paiement (refonte : § 12 du doc).
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [validation, setValidation] = useState<string | null>(null);
  const [state, setState] = useState<PayState>('idle');
  const [payError, setPayError] = useState<string | null>(null);

  const [slot, setSlot] = useState<SlotState>(bookingUid ? 'checking' : 'none');
  const [booking, setBooking] = useState<CoachingBooking | null>(null);
  const [checkAttempt, setCheckAttempt] = useState(0); // incrémenté par « Réessayer »
  const unmounted = useRef(false);

  useEffect(() => () => { unmounted.current = true; }, []);

  // Pré-remplissage dès que le profil est chargé (sans écraser une saisie en cours).
  useEffect(() => {
    if (!profile) return;
    const full = [profile.first_name, profile.last_name].map((v) => v?.trim()).filter(Boolean).join(' ');
    setName((prev) => prev || full || profile.full_name?.trim() || '');
    setEmail((prev) => prev || profile.email || getAuthEmail() || '');
  }, [profile]);

  // Le webhook Cal.com part APRÈS la redirection de confirmation : on attend qu'il ait écrit la
  // ligne avant d'ouvrir le paiement. Tant qu'elle n'existe pas, rien ne prouve qu'un créneau a
  // été posé dans l'agenda — et payer sans rendez-vous est le seul scénario vraiment inacceptable.
  useEffect(() => {
    if (!bookingUid) return;
    let stale = false;
    setSlot('checking');
    void (async () => {
      const found = await waitForCoachingBooking(bookingUid, { isCancelled: () => stale || unmounted.current });
      if (stale || unmounted.current) return;
      if (!found) return setSlot('missing');
      setBooking(found);
      setSlot(found.status === 'cancelled' ? 'cancelled' : 'confirmed');
    })();
    return () => { stale = true; };
  }, [bookingUid, checkAttempt]);

  // Sans bookingUid on garde l'ancien comportement (aucun créneau à vérifier) ; avec, le paiement
  // reste fermé tant que le serveur n'a pas confirmé.
  // La disponibilité de la plateforme rejoint les autres causes de blocage : le bouton est alors
  // désactivé, et l'encart ci-dessous dit pourquoi — plutôt qu'un bouton qui échouerait à coup sûr.
  const payBlocked =
    !PAIEMENT_DISPONIBLE ||
    // TARIF NON CHARGÉ = BOUTON INERTE. Payer un montant qu'on n'a pas su afficher, c'est
    // débiter quelqu'un sans lui avoir annoncé la somme.
    !tarifPret(etatTarifs, formule) ||
    slot === 'checking' ||
    slot === 'missing' ||
    slot === 'cancelled' ||
    // DOUBLE ENCAISSEMENT : après une issue « en cours » ou « réussi », un second appui créait une
    // seconde intention donc un second débit. Le bouton reste fermé tant qu'une tentative est en
    // vol ou qu'elle a abouti ; `processing` est le cas où la banque n'a pas encore tranché.
    state === 'paying' ||
    state === 'processing' ||
    state === 'paid';

  const onPay = async () => {
    setValidation(null);
    setPayError(null);
    if (payBlocked) return;
    if (!name.trim()) return setValidation(t('coaching.nameRequired'));
    if (!isValidEmail(email)) return setValidation(t('coaching.invalidEmail'));

    setState('paying');
    try {
      // 1. Le serveur crée le PaymentIntent (montant figé côté serveur) → client_secret.
      // Seule la formule part : nom, e-mail et message ne quittent plus l'appareil pour le paiement.
      const intent = await createCoachingPaymentIntent(formule);

      /**
       * 2. LE PRIX ANNONCÉ EST-IL CELUI QU'ON VA DÉBITER ?
       *
       * Le montant vient du serveur, le prix affiché d'une constante du dépôt : deux sources pour
       * la même somme, recopiées à la main l'une depuis l'autre. La duplication PRÉEXISTE à ce lot
       * et n'y est pas réparée — mais elle cesse d'être silencieuse. Débiter un montant que
       * personne n'a annoncé est une pratique trompeuse ; on refuse plutôt que d'ouvrir la feuille.
       *
       * Le PaymentIntent déjà créé reste orphelin, ce qui est le moindre mal : il n'est pas
       * confirmé, donc rien n'est débité, et l'incident dit quoi corriger.
       */
      /**
       * LE MONTANT COMPARÉ EST CELUI QUI A ÉTÉ AFFICHÉ, lu dans l'état du composant — pas une
       * relecture du catalogue. Relire ici reviendrait à comparer le serveur à lui-même : la
       * garde n'aurait plus aucun pouvoir tout en ayant l'air d'en avoir un.
       */
      const verdict = verifierMontant(tarifDe(etatTarifs, formule)?.amount ?? null, intent.amount);
      if (!verdict.coherent) {
        capturerIncident(verdict.code ?? 'coaching_montant_divergent', 'paiement');
        setPayError(t('coaching.montantIncoherent'));
        setState('error');
        return;
      }

      // 3. Une seule demande, quelle que soit la plateforme : le contrat porte la différence.
      const issue = await payer({ clientSecret: intent.client_secret, returnUrl: URL_RETOUR_PAIEMENT() });

      // 4. Lit l'issue. Quatre cas + la redirection, et AUCUN n'est déduit d'un autre.
      if (issue.etat === 'redirige') {
        // Le navigateur est parti chez le moyen de paiement. Rien à afficher : la route de retour
        // prendra le relais. Ne surtout pas annoncer un échec — le paiement est en cours.
        return;
      }
      if (issue.etat === 'succeeded') return setState('paid');
      if (issue.etat === 'canceled') return setState('canceled');
      if (issue.etat === 'processing') return setState('processing');

      setPayError(issue.message ?? t('coaching.paymentFailedShort'));
      setState('error');
      capturerIncident('coaching_paiement_refuse', 'paiement');
    } catch (e) {
      // Le message du prestataire ne s'affiche PAS tel quel et ne part PAS chez Sentry : on
      // remonte un code écrit ici, comme partout ailleurs depuis le lot du 19/09.
      capturerIncident('coaching_paiement_exception', 'paiement');
      setPayError(t('coaching.paymentFailed'));
      setState('error');
    }
  };

  const slotDate = booking?.starts_at
    ? new Date(booking.starts_at).toLocaleString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  const { large } = useGabarit();
  const sCreneau = (
    <>
        {slot === 'checking' ? (
          <View style={[styles.slotBox, styles.slotNeutral]} testID="coaching-res-slot-checking">
            <ActivityIndicator color={colors.electric} />
            <Text style={styles.slotText}>{t('coaching.checkingSlot')}</Text>
          </View>
        ) : null}

        {slot === 'confirmed' ? (
          <View style={[styles.slotBox, styles.slotOk]} testID="coaching-res-slot">
            <CalendarCheck size={18} color={colors.success} strokeWidth={2} />
            <Text style={styles.slotText}>
              {slotDate
                ? `Créneau confirmé : ${slotDate}. Il ne reste qu’à valider le paiement.`
                : t('coaching.slotConfirmed')}
            </Text>
          </View>
        ) : null}

        {slot === 'missing' ? (
          <View style={[styles.slotBox, styles.slotWarn]} testID="coaching-res-slot-missing">
            <TriangleAlert size={18} color={colors.warning} strokeWidth={2} />
            <View style={styles.slotBody}>
              <Text style={styles.slotText}>
                On ne retrouve pas ton créneau. Le paiement reste fermé tant qu’il n’est pas confirmé :
                tu ne peux pas payer une session sans rendez-vous.
              </Text>
              <Pressable
                accessibilityRole="button"
                onPress={() => setCheckAttempt((n) => n + 1)}
                testID="coaching-res-slot-retry"
              >
                <Text style={styles.retry}>{t('common.retry')}</Text>
              </Pressable>
            </View>
          </View>
        ) : null}

        {slot === 'cancelled' ? (
          <View style={[styles.slotBox, styles.slotWarn]} testID="coaching-res-slot-cancelled">
            <TriangleAlert size={18} color={colors.error} strokeWidth={2} />
            <Text style={styles.slotText}>
              Ce créneau a été annulé. Reviens en arrière pour en choisir un autre.
            </Text>
          </View>
        ) : null}

            </>
  );
  const sRecap = (
    <>
<Card testID="coaching-res-recap">
          <Text style={styles.recapLabel}>{t('coaching.chosenFormule')}</Text>
          <View style={styles.recapRow}>
            <Text style={styles.recapTitle}>{f?.title ?? formule}</Text>
            {libellePrix(etatTarifs, formule) ? (
              <Text testID="coaching-res-prix" style={styles.recapPrice}>
                {libellePrix(etatTarifs, formule)}
              </Text>
            ) : null}
          </View>
        </Card>

            </>
  );
  const sChamps = (
    <>
<View style={styles.field}>
          <Text style={styles.label}>NOM</Text>
          <TextInput testID="coaching-res-name" style={styles.input} value={name} onChangeText={setName} placeholder={t('misc.coachNamePlaceholder')} placeholderTextColor={colors.grey400} />
        </View>
        <View style={styles.field}>
          <Text style={styles.label}>{t('coaching.email')}</Text>
          <TextInput
            testID="coaching-res-email"
            style={styles.input}
            value={email}
            onChangeText={setEmail}
            placeholder="ton@email.com"
            placeholderTextColor={colors.grey400}
            keyboardType="email-address"
            autoCapitalize="none"
          />
        </View>
        <View style={styles.field}>
          <Text style={styles.label}>{t('coaching.searching')}</Text>
          <TextInput
            testID="coaching-res-message"
            style={[styles.input, styles.inputMultiline]}
            value={message}
            onChangeText={setMessage}
            placeholder={t('misc.coachGoalPlaceholder')}
            placeholderTextColor={colors.grey400}
            multiline
          />
        </View>

            </>
  );
  const sPaiement = (
    <>
{validation ? (
          <Text testID="coaching-res-validation" style={styles.error}>
            {validation}
          </Text>
        ) : null}

        {state === 'paid' ? (
          <View testID="coaching-res-paid" style={styles.sentBox}>
            <Text style={styles.sentText}>{t('coaching.paid')}</Text>
          </View>
        ) : (
          <>
            <Button
              // Sans tarif connu, le bouton dit « Payer » tout court — il est de toute façon
              // inerte. Un montant de repli dans un libellé de paiement serait la pire place.
              title={`Payer ${libellePrix(etatTarifs, formule) ?? ''}`.trim()}
              withArrow
              disabled={payBlocked}
              loading={state === 'paying'}
              onPress={onPay}
              testID="coaching-res-pay"
            />
            {/* LE FORMULAIRE STRIPE, monté par le shim. Inerte sur mobile — la feuille y est
                présentée par le système. Sur le web il apparaît une fois le PaymentIntent créé et
                le montant vérifié, et c'est lui qui porte la confirmation. */}
            <Formulaire />
            {/* UN BOUTON DÉSACTIVÉ DOIT DIRE POURQUOI. Sans cet encart, le web n'aurait qu'une
                cible grisée sans explication — le « bouton mort » qu'on ferme partout ailleurs.
                Même parti que le Paywall, qui remplace ses CTA par une phrase quand les achats ne
                sont pas branchés sur la plateforme. */}
            {!PAIEMENT_DISPONIBLE ? (
              <Text testID="coaching-res-unavailable" style={styles.note}>
                {t('coaching.payUnavailable')}
              </Text>
            ) : null}
            {state === 'processing' ? (
              // LA BANQUE N'A PAS ENCORE TRANCHÉ. Sans ce message, le bouton restait là, fermé, sans
              // un mot — et la seule action qui s'offrait à la personne était d'aller payer ailleurs.
              <Text testID="coaching-res-processing" style={styles.note}>
                {t('coaching.processing')}
              </Text>
            ) : null}
            {state === 'canceled' ? (
              <Text testID="coaching-res-canceled" style={styles.note}>
                Paiement annulé. Ton créneau reste réservé, tu peux payer quand tu veux.
              </Text>
            ) : null}
            {state === 'error' ? (
              <View testID="coaching-res-error" style={styles.errorBox}>
                <Text style={styles.error}>{payError ?? t('coaching.paymentFailedShort')} Réessaie.</Text>
              </View>
            ) : null}
          </>
        )}

        <Text style={styles.note}>{t('coaching.securePayment')}</Text>
    </>
  );

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <ScreenHeader onBack={onBack} title={t('coaching.reserve')} testID="coaching-res-back" />
              {large ? (
          <ScrollView contentContainerStyle={styles.colonnesL} showsVerticalScrollIndicator={false}>
            <View style={styles.colGaucheL}>
              {sCreneau}
              {sChamps}
            </View>
            <View style={styles.colDroiteL}>
              {sRecap}
              {sPaiement}
            </View>
          </ScrollView>
        ) : (
          <ScrollView contentContainerStyle={[styles.content, colonneFormulaire]} keyboardShouldPersistTaps="handled">
            {sCreneau}
            {sRecap}
            {sChamps}
            {sPaiement}
          </ScrollView>
        )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  colonnesL: {
    flexGrow: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.xl,
    width: '100%',
    maxWidth: layout.pageMaxWidth,
    alignSelf: 'center',
    padding: spacing.xxl + spacing.xl,
  },
  colGaucheL: { flex: 1, minWidth: 0, gap: spacing.lg },
  colDroiteL: { flex: 1, minWidth: 0, gap: spacing.md },
  piedL: { marginTop: spacing.sm },
  container: { flex: 1, backgroundColor: colors.grey50 },
  content: { padding: screenPadding, gap: spacing.md },
  slotBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    padding: spacing.md,
    borderRadius: radius.lg,
  },
  slotNeutral: { backgroundColor: colors.blueSoft },
  slotOk: { backgroundColor: colors.successSoft },
  slotWarn: { backgroundColor: colors.warningSoft },
  slotBody: { flex: 1, gap: spacing.sm },
  slotText: { flex: 1, fontSize: typography.small.size, lineHeight: 18, color: colors.grey700 },
  retry: { fontSize: typography.small.size, fontWeight: '700', color: colors.electric },
  recapLabel: { fontSize: typography.caption.size, fontWeight: typography.caption.weight, letterSpacing: typography.caption.letterSpacing, color: colors.grey500 },
  recapRow: { flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between', marginTop: spacing.sm },
  recapTitle: { fontSize: typography.h3.size, fontWeight: typography.h3.weight, color: colors.grey900 },
  recapPrice: { fontSize: typography.h3.size, fontWeight: '700', color: colors.bluePrimary },
  field: { gap: spacing.xs },
  label: { fontSize: typography.caption.size, fontWeight: typography.caption.weight, letterSpacing: typography.caption.letterSpacing, color: colors.grey500 },
  input: {
    minHeight: 48,
    borderWidth: 1,
    borderColor: colors.grey200,
    borderRadius: radius.md,
    padding: spacing.md,
    fontSize: typography.body.size,
    color: colors.grey900,
    backgroundColor: colors.white,
  },
  inputMultiline: { minHeight: 88, textAlignVertical: 'top' },
  error: { color: colors.error, fontSize: typography.small.size },
  errorBox: { gap: spacing.sm },
  sentBox: { gap: spacing.sm, padding: spacing.lg, borderRadius: radius.lg, backgroundColor: colors.greenSoft },
  sentText: { fontSize: typography.bodyMd.size, fontWeight: typography.bodyMd.weight, color: colors.green },
  note: { fontSize: typography.small.size, color: colors.grey400 },
});
